import { createClient, type RealtimeChannel } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';
import camelcaseKeys, { type CamelCaseKeys } from 'camelcase-keys';
import type { DatabaseTables, RowOfTable } from '@/types/utility-types';
import fetchRetry from 'fetch-retry';
import { isNonNullable } from '@/utils/nullability-helpers';

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Returns a fetch function that retries on 5xx status codes with exponential backoff.
 */
const fetchWithRetry = fetchRetry(fetch, {
    retries: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000),
    retryOn: (attempt: number, error: Error | null, response?: Response | null) => {
        // Retry on any 5xx status code
        // Status codes 4** are auth issues, so even if we retry, it will fail again.
        // Only retry up to 3 times (including the initial request)
        if (isNonNullable(response) && response.status >= 500 && response.status < 600 && attempt < 3) {
            return true;
        }
        return false;
    },
});

export const supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    global: {
        fetch: fetchWithRetry,
    },
});

export type SupabaseClient = typeof supabaseClient;

export const supabaseFunctionsUrl = (() => {
    const isPlatform = supabaseUrl.match(/(supabase\.co)|(supabase\.in)/);
    if (isPlatform) {
        const urlParts = supabaseUrl.split('.');
        return `${urlParts[0]}.functions.${urlParts[1]}.${urlParts[2]}`;
    } else {
        return `${supabaseUrl}/functions/v1`;
    }
})();

type RealtimeFilters = 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte';

type DatabaseRealtimeFilter<T extends DatabaseTables> = `${keyof RowOfTable<T> & string}=${RealtimeFilters}.${string}`;

export function listenToTable<
    T extends DatabaseTables,
    C extends CamelCaseKeys<RowOfTable<T>> = CamelCaseKeys<RowOfTable<T>>,
>(table: T, onChange: (data?: C) => void, filter?: DatabaseRealtimeFilter<T>): RealtimeChannel {
    return supabaseClient
        .channel(`public:${table}`)
        .on<RowOfTable<T>>('postgres_changes', { event: '*', schema: 'public', table, filter }, (payload) => {
            if (payload.eventType === 'DELETE') {
                onChange(undefined);
                return;
            }
            onChange(camelcaseKeys(payload.new) as C);
        })
        .subscribe();
}
